import type { UseQueryOptions } from "@tanstack/react-query";

export interface Workspace {
  id: string;
  name: string;
  description: string;
  privacyPolicy: string;
  color: string;
  logo?: string;
}

export interface Organisation {
  id: string;
  slug?: string;
  name: string;
  workspaces: string[];
  phoneNumber?: string;
  email?: string;
  address?: string;
  website?: string;
  logo?: string;
  linkedInUrl?: string;
  headOfficeLocation?: string;
  officeLocations?: string[];
  paidStaffLocations?: string[];
  bio?: string;
  mission?: string;
  yearFounded?: Date;
  media?: {
    id: string;
    slug: string;
    name: string;
    description: string;
    url?: string;
  }[];
  contactPerson: {
    name: string;
    email: string;
    bio?: string;
    linkedInUrl?: string;
  };
  isActive: boolean;
  emailVerified: boolean;
  createdAt: Date;
  updatedAt: Date;
  verified: boolean;
}

export interface BulkOrganisationPayload extends Partial<Organisation> {
  workspaceIds: string[];
  map?: {
    category: string;
    tags: string[];
  }[];
}

export interface MapCategory {
  id: string;
  name: string;
  workspace: string; // workspace id
  tags: string[];
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface MapData {
  id: string;
  category: string; // category id
  organisation:
    | {
        id: string;
        name: string;
      }
    | string; // organisation id or object
  tags: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface OrganisationMedia {
  url: string;
  name: string;
  description: string;
}

// Minimal project data for list view
export interface ProjectListItem {
  id: string;
  title: string;
  timeline: string;
  status: (typeof ProjectStatus)[keyof typeof ProjectStatus];
  organisation: {
    id: string;
    name: string;
    logo?: string;
  };
  createdAt: Date;
  updatedAt: Date;
}

// Full project data for detail view
export interface Project {
  id: string;
  title: string;
  summary: string;
  timeline: string;
  tags: string[];
  media: {
    name: string;
    url: string;
  };
  organisation: Organisation;
  collaboratingOrganisations: string[];
  status: (typeof ProjectStatus)[keyof typeof ProjectStatus];
  continent: string;
  country: string;
  region: string;
  state: string;
  keyOutcomes: string;
  createdAt: Date;
  updatedAt: Date;
}

export const ProjectStatus = {
  PENDING: "pending",
  COMPLETED: "completed",
} as const;

export interface Pagination {
  totalDocs: number;
  limit: number;
  totalPages: number;
  page: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  nextPage: number;
  prevPage: number;
}

export interface Filter {
  page?: number;
  limit?: number;
  search?: string;
  sort?: string;
}

export interface User {
  id: string;
  email: string;
  fullName: string;
  workspace: Workspace | null; // user can only have one workspace
  organisation: Organisation | null; // user can only have one organisation
  systemRole: string;
  phone?: string;
  phoneVerified: boolean;
  emailVerified: boolean;
  linkedInUrl: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}
export interface Inquiry {
  id: string;
  subject?: string;
  message: string;
  telephone?: string;
  email: string;
  status: "pending" | "resolved";
  user: User;
  workspace: string;
  response: string | null;
  resolvedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export type AllUserRoles =
  | "org_admin"
  | "org_user"
  | "workspace_admin"
  | "workspace_user";
export interface Invitation {
  email: string;
  role: AllUserRoles;
  context: "organisation" | "workspace";
  workspaceId?: string;
  organisationId?: string;
  createdAt?: Date;
  updatedAt?: Date;
  expiresInDays?: number;
}

export const NotificationAudience = {
  USER: "User",
  ORGANISATION: "Organisation",
  WORKSPACE: "Workspace",
} as const;

export type NotificationAudienceType =
  (typeof NotificationAudience)[keyof typeof NotificationAudience];
export interface Notification {
  id: string;
  title: string;
  message: string;
  url?: string;
  audience: NotificationAudienceType;
  scope: string;
  createdAt: Date;
}
export interface NotificationStatus {
  id: string;
  notification: Notification;
  entity: string; // entity id (user/organisation/workspace)
  entityType: NotificationAudienceType;
  read: boolean;
  readAt?: Date;
  createdAt?: Date;
}

export type CustomQueryOptions<
  TQueryFnData,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends readonly unknown[] = [string, string],
> = Omit<
  UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  "queryKey" | "queryFn"
>;

// External requests types
export interface Country {
  code: string;
  name: string;
  flag: string;
  dial_code: string;
  continent: string;
  region: string;
  subregion: string;
}

export interface Opportunity {
  id: string;
  title: string;
  summary: string;
  url?: string;
  media?: {
    url: string;
    name: string;
  };
  // Legacy field - kept for backward compatibility
  organisation?:
    | {
        id: string;
        name: string;
      }
    | string;
  // New fields from API response
  ownerEntity?: {
    id: string;
    name: string;
    email: string;
  };
  ownerType?: string;
  createdBy?: string;
  submissionsCount?: number;
  workspace?: string;
  status?: string;
  settings?: SettingsData;
  createdAt: string;
  updatedAt: string;
}

export interface OpportunityApplication {
  id: string;
  contactPersonName: string;
  contactPersonEmail: string;
  contactPersonPhone?: string;
  url?: string;
  experienceFile?: string;
  customResponses?: Array<{
    questionId: string;
    questionLabel: string;
    response?: string | number | string[];
    files?: Array<{
      name: string;
      url: string;
    }>;
  }>;
  status: string;
  createdAt: string;
  updatedAt: string;
  submittedBy: string;
}

export type QuestionType =
  | "singleselect"
  | "multiselect"
  | "url"
  | "file"
  | "number"
  | "textarea"
  | "text";

export type CustomQuestion = {
  name: string;
  label: string;
  type: QuestionType;
  required: boolean;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  helpText?: string;
  fileConfig?: {
    maxFiles: number;
    allowedFormats: string[];
  };
  numberConfig?: {
    min: number;
    max: number;
  };
  textConfig?: {
    maxLength: number;
  };
  order: number;
};

export type SettingsData = {
  submissionDeadline: string; // ISO string
  confirmationEmail?: string; // Optional - may not be in API response
  notificationEmails?: string[]; // Optional - may not be in API response
  allowApplicationUpdate?: boolean; // From API response
};
